import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import '../css/ArticleDetail.css';
import {
  fetchArticleById,
  fetchLikesForArticle,
  addLikeToArticle,
  removeLikeFromArticle
} from '../api/reviewAPI';
import LikeButton from './LikeButton.js';

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const DetailItem = ({ label, value }) => (
  value ? (
    <div className="detail-item">
      <span className="detail-label">{label}</span>
      <strong>{value}</strong>
    </div>
  ) : null
);

const DetailSection = ({ title, content }) => (
  content ? (
    <section className="detail-section">
      <h2>{title}</h2>
      <p>{content}</p>
    </section>
  ) : null
);

const DetailListSection = ({ title, content }) => (
  content ? (
    <section className="detail-section">
      <h2>{title}</h2>
      <div className="article-content whitespace-copy">{content}</div>
    </section>
  ) : null
);

const ArticleDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [likesCount, setLikesCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      setError('');

      try {
        const data = await fetchArticleById(id);
        setArticle(data);

        const token = localStorage.getItem('token');
        setIsLoggedIn(Boolean(token));
        const response = await fetchLikesForArticle(id, token);
        setLikesCount(response.count);
        setHasLiked(response.hasLiked);
      } catch (loadError) {
        setError(loadError.message || 'Failed to fetch article');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [id]);

  const interviewDetails = useMemo(() => article?.interviewDetails || {}, [article]);
  const summaryItems = [
    { label: 'Company', value: article?.companyName },
    { label: 'Role', value: article?.role },
    { label: 'Location', value: article?.location },
    { label: 'Interview date', value: formatDate(interviewDetails.interviewDate) || article?.year },
    { label: 'Experience level', value: interviewDetails.experienceLevel },
    { label: 'Difficulty', value: interviewDetails.difficulty },
    { label: 'Outcome', value: interviewDetails.outcome },
    { label: 'Applied via', value: interviewDetails.applicationSource },
    { label: 'Compensation', value: interviewDetails.compensation },
  ].filter((item) => item.value);

  const handleLike = async () => {
    try {
      await addLikeToArticle(id);
      setHasLiked(true);
      setLikesCount((count) => count + 1);
    } catch (likeError) {
      setError(likeError.message || 'Failed to like the article');
    }
  };

  const handleUnlike = async () => {
    try {
      await removeLikeFromArticle(id);
      setLikesCount((count) => count - 1);
      setHasLiked(false);
    } catch (likeError) {
      setError(likeError.message || 'Failed to unlike the article');
    }
  };

  if (loading) {
    return (
      <div className="article-detail-shell">
        <div className="article-detail-card">
          <p>Loading interview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="article-detail-shell">
        <div className="article-detail-card">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <main className="article-detail-shell">
      <article className="article-detail-card">
        <div className="article-detail-hero">
          <div className="article-detail-main">
            <span className="detail-eyebrow">Interview report</span>
            <h1 className="article-title">{article?.title}</h1>
            <p className="detail-subtitle">
              A structured breakdown of the process, preparation, and outcome.
            </p>
            <div className="article-meta-grid">
              {summaryItems.map((item) => (
                <DetailItem key={item.label} label={item.label} value={item.value} />
              ))}
            </div>
          </div>

          <aside className="detail-side-panel">
            <LikeButton
              likesCount={likesCount}
              isLoggedIn={isLoggedIn}
              hasLiked={hasLiked}
              handleLike={handleLike}
              handleUnlike={handleUnlike}
            />
            {article?.user?.username && !article?.anonymous && (
              <p className="author-line">
                Shared by <Link to={`/user/${article.user.username}`}>@{article.user.username}</Link>
              </p>
            )}
          </aside>
        </div>

        <section className="detail-section detail-story">
          <h2>Full experience</h2>
          <div className="article-content whitespace-copy">{article?.text}</div>
        </section>

        <section className="detail-grid">
          <DetailListSection title="Interview rounds" content={interviewDetails.rounds} />
          <DetailListSection title="Questions asked" content={interviewDetails.questions} />
          <DetailSection title="Preparation strategy" content={interviewDetails.preparationTime} />
          <DetailListSection title="Candidate tips" content={interviewDetails.tips} />
        </section>

        {!interviewDetails.rounds && !interviewDetails.questions && !interviewDetails.tips && (
          <section className="detail-section detail-empty-state">
            <h2>Structured details are still sparse</h2>
            <p>
              This report already captures the overall experience, but it does not yet include
              extra round-by-round notes or candidate tips.
            </p>
          </section>
        )}
      </article>
    </main>
  );
};

export default ArticleDetail;
